export interface BrandItem {
  brandId: string
  brandName: string
}

// LP8658 박스단위 입고 정산 — 단위 타입 단일 출처. serviceChargeItem.ts 가 이 타입을 import 한다.
export type UnitType = 'PCS' | 'BOX'

// LOGISPRD-10268 — 프로모션 고도화 신규 축 (BE PR-3 계약: LLD-LP10200 §6, dev 스웨거 반영 전 임시 정의)
export type PromotionCountry = 'KR' | 'JP'
// LOGISPRD-11315 — 주문유형 축. 국가와 짝을 이뤄 부과 기준(anchor) 한 행을 고른다
// (한국+국내 = 국내 · 한국+해외 = GPC · 일본+해외 = 일본 전진배치. 일본+국내는 anchor 가 없다)
export type PromotionOrderType = 'DOMESTIC' | 'GLOBAL'
export type SettlementCycle = 'DAILY' | 'MONTHLY'
// 기간 프로모션 구간의 기준 축 — 현재 수량(QUANTITY) 하나뿐 (BE PromotionTierType)
export type PromotionTierType = 'QUANTITY'
export type DiscountType = 'RATE' | 'FIXED'
// 적용 단위 — LLD-LP10200 §3. BE PromotionTargetUnit 9값 (BE 가 4축 조합으로 판정하되, 화면은 상품 축을 보내지 않는다). 검색은 이 값의 목록으로 거른다
export const PROMOTION_TARGET_UNITS = [
  'ALL',
  'PARTNER',
  'BRAND',
  'CATEGORY',
  'PRODUCT',
  'PARTNER_BRAND',
  'PARTNER_CATEGORY',
  'BRAND_CATEGORY',
  'PARTNER_BRAND_CATEGORY',
] as const

export type PromotionTargetUnit = (typeof PROMOTION_TARGET_UNITS)[number]

// 월 누적 수량 구간별 할인 (promotion_tier). toValue null = 무한(이상)
// 필드명은 BE 계약(PromotionTierRequestDto, LOGISPRD-11164)을 따른다
export interface PromotionTier {
  fromValue: number
  toValue: number | null
  discountValue: string
}

export interface PostPromotionReq {
  serviceType: string
  unitType: UnitType // LP8658 박스단위 입고 정산 — INBOUND PCS/BOX 분화 (그 외 serviceType 은 항상 PCS)
  title: string
  partnerIds: string[]
  applyToAllPartners: boolean
  brands: BrandItem[]
  applyToAllBrands: boolean
  discountType: string
  discountValue: string
  startDate: string
  endDate: string
  // LOGISPRD-10268 — 신규 축 (BE feature/LOGISPRD-10268 반영 완료 계약)
  // targetUnit 은 요청에 보내지 않는다 — BE 가 축 조합에서 판정 (PromotionTargetUnit.determine)
  country?: PromotionCountry // 서비스 항목과 함께 부과 기준(anchor) 결정 (BE PromotionSaveRequestDto.country)
  orderType?: PromotionOrderType // LOGISPRD-11315 — 미전송이면 BE 가 국가로 읽는다 (KR→DOMESTIC · JP→GLOBAL)
  settlementCycle?: SettlementCycle // BE 는 현재 DAILY 만 허용 (MONTHLY 는 Tier 테이블 준비 후)
  categoryCodes?: string[] // 빈 배열/미전송 = 전체. 지정 시 카테고리 축
  tiers?: PromotionTier[] // MONTHLY 전용 (LOGISPRD-11164)
  tierType?: PromotionTierType // 미전송이면 BE 가 MONTHLY 일 때 QUANTITY 로 채운다 (PromotionSaveRequestDto.kt:123)
  remark?: string // LOGISPRD-11340 — 비고. 선택 입력이라 빈 값은 미전송
  skuIds?: string[] // LOGISPRD-13627 — 상품 종류만 보낸다. 서버는 선정 FF 와 SKU 대상 FF 가 모두 켜져야 받는다
}

export interface PostPromotionRes {
  id: string
  title: string
  partnerId: string
  brands: BrandItem[]
  discountType: string
  discountValue: string
  startDate: string
  endDate: string
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}

export interface PostBrandListRes {
  partnerBrands: {
    partnerId: string
    brands: BrandItem[]
  }[]
  // LOGISPRD-10268 — 업체 구분 없는 브랜드 목록 (BE 가 brandId 기준 중복 제거해 내려줌)
  brands: BrandItem[]
}

export type ServiceTypeCode =
  | 'INVENTORY'
  | 'INBOUND'
  | 'OUTBOUND_TO_CUSTOMER'
  | 'OUTBOUND_TO_STORE'
  | 'OUTBOUND_SHIPPING'
  | 'OUTBOUND_TO_CUSTOMER_SHIPPING'
  | 'OUTBOUND_TO_STORE_SHIPPING'
  | 'RELEASE'
  | 'RETURN_INBOUND'
  | 'RETURN_INBOUND_SHIPPING'
  | 'REMOTE_AREA_SHIPPING'
  | 'ETC_ADJUSTMENT'
  // LOGISPRD-16794 실측비 (프로모션 비대상)
  | 'MEASUREMENT'

// 택배비류 전체 — 업체·브랜드 단위까지만 (기획 확정 2026-07-22 · LLD-LP10200 §6).
// 레거시(OUTBOUND_SHIPPING)와 분화 타입을 모두 포함해야 카테고리 게이트에 구멍이 없다 (검증 리포트 DEF-05)
// 도서산간비도 같은 부과 기준이다 (PromotionValidator.kt:111 — "송장 단위(charge_base_type=INVOICE — 택배비류·도서산간비)")
// 이 목록을 화면별로 다시 선언하지 않는다. v2 스키마가 자체 선언해 두 항목이 빠진 적이 있다
export const PARCEL_SERVICE_TYPES: ServiceTypeCode[] = [
  'OUTBOUND_SHIPPING',
  'OUTBOUND_TO_CUSTOMER_SHIPPING',
  'OUTBOUND_TO_STORE_SHIPPING',
  'RETURN_INBOUND_SHIPPING',
  'REMOTE_AREA_SHIPPING',
]

export interface GetServiceTypeListRes {
  code: ServiceTypeCode
  name: string
}

// 등록 화면 상품 종류(SKU ID) 노출 플래그 (LOGISPRD-13627). 서버 게이트는 MASS `feature.skuAxisCutover.promotionSkuTarget` 이 따로 막는다
export const PROMOTION_SKU_TARGET_FLAG = { key: 'feature.skuIdTransition', objectKey: 'promotionSkuTarget' } as const

/**
 * LOGISPRD-11315 — 등록 가능한 부과 기준(anchor) 조합 (GET /v1/promotions/charge-bases).
 * service_charge_base 에 실재하는 행만 내려오므로, 등록 모달의 국가·주문유형·서비스 항목 드롭다운을 이 목록으로 채우면
 * anchor 없는 조합(일본+국내 등)은 애초에 선택되지 않는다.
 */
export interface GetPromotionChargeBaseRes {
  country: CodeNameMappingType
  orderType: CodeNameMappingType
  serviceType: CodeNameMappingType
  unitType: UnitType
  chargeBaseType: CodeNameMappingType
}

// 프로모션명 상한 — PRD 기준 50자 (LOGISPRD-11339). 저장 컬럼 promotion.title 은 varchar(100)
export const PROMOTION_TITLE_MAX_LENGTH = 50

// 비고 상한 — PRD 기준 100자 (LOGISPRD-11340). 저장 컬럼 promotion.remark 도 varchar(100)
export const PROMOTION_REMARK_MAX_LENGTH = 100

export type PromotionStatusCode = 'PENDING' | 'ACTIVE' | 'ENDED'

export interface GetPromotionStatusListRes {
  code: PromotionStatusCode
  name: string
}

export interface PostPromotionListReq {
  serviceType: string[] | null
  unitType?: UnitType | null // LP8658 — INBOUND 검색 시 PCS/BOX 필터. 비-INBOUND/전체는 null (BE LOGISPRD-8524 search filter)
  status: string | null
  promotionStartDate?: string
  promotionEndDate?: string
  createdAtFrom?: string
  createdAtTo?: string
  partnerIds?: string[] | null
  brandIds: string[] | null
  // LOGISPRD-10268 — 검색 필터 확장 (LLD-LP10200 §6: country·settlementCycle·discountType·barcode)
  // country·discountType 은 BE PromotionSearchRequestDto 에 아직 없어 전송해도 걸리지 않는다 (settlementCycle·productBarcode·categoryCode 는 반영됨)
  country?: PromotionCountry | null
  settlementCycle?: SettlementCycle | null
  discountType?: DiscountType | null
  productBarcode?: string | null
  categoryCode?: string | null
  // LOGISPRD-11309 — 프로모션 종류 필터. 종류 하나가 적용 단위 여러 값에 대응해 목록으로 보낸다
  targetUnits?: PromotionTargetUnit[] | null
}

interface CodeNameMappingType {
  code: string
  name: string
}

export interface PostPromotionListRes {
  id: number
  serviceType: CodeNameMappingType
  // LP8658 — BE(LOGISPRD-8524) 검색 응답에 추가 예정. 미배포 구간 대비 optional, 표시 시 'PCS' 가드 (기존 프로모션 = PCS)
  unitType?: UnitType
  promotionBaseDateType: CodeNameMappingType
  promotionChargeBase: CodeNameMappingType
  title: string
  // 전체 대상 프로모션은 null 로 내려온다 (LOGISPRD-10268 — 표시는 '전체') — non-null 로 두면 가드 누락을 컴파일러가 못 잡음
  partnerIds: string[] | null
  brands: BrandItem[] | null
  discountType: CodeNameMappingType
  status: CodeNameMappingType
  discountValue: string
  startDate: string
  endDate: string
  editable: boolean
  createdBy: string
  createdAt: string
  updatedBy: string
  updatedAt: string
  // LOGISPRD-10268 — 신규 축 (BE PR-3 검색 응답 반영 전 optional)
  // LOGISPRD-11315 로 code/name 객체로 내려온다 (그 전 배포분은 미전송)
  country?: CodeNameMappingType
  orderType?: CodeNameMappingType
  settlementCycle?: SettlementCycle
  targetUnit?: PromotionTargetUnit
  productBarcodes?: string[]
  categoryCodes?: string[]
  tiers?: PromotionTier[]
  // LOGISPRD-11340 — 비고. 입력하지 않은 건과 BE 미배포 구간은 내려오지 않는다
  remark?: string | null
}

export interface PutPromotionReq {
  promotionId: number
  serviceType: string
  unitType: UnitType // LP8658 — INBOUND PCS/BOX 분화 (그 외 PCS)
  title: string
  partnerIds: string[]
  applyToAllPartners: boolean
  brands: BrandItem[]
  applyToAllBrands: boolean
  discountType: string
  discountValue: string
  startDate: string
  endDate: string
  // LOGISPRD-10268 — 신규 축 (PostPromotionReq 와 동일 계약, targetUnit 은 BE 판정)
  country?: PromotionCountry
  orderType?: PromotionOrderType // LOGISPRD-11448 — 수정도 이 축으로 anchor 를 다시 해소한다
  settlementCycle?: SettlementCycle
  categoryCodes?: string[]
  tiers?: PromotionTier[]
  tierType?: PromotionTierType
  remark?: string // LOGISPRD-11340 — 비고. 선택 입력이라 빈 값은 미전송
}

export interface PutPromotionRes {
  id: number
  title: string
  partnerIds: string[]
  brands: BrandItem[]
  discountType: string
  discountValue: string
  serviceType: ServiceTypeCode
  startDate: string
  endDate: string
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
}
export interface PatchStopPromotionRes {
  stopped: number[] // 정상적으로 중지된 프로모션 ID 리스트
  alreadyStopped: number[] // 이미 중지된 프로모션 ID 리스트
  notFound: number[] // 존재하지 않는 프로모션 ID 리스트
}

// LOGISPRD-10354 — 프로모션 할인 조회 (mass /v1/promotions/settlements/*)
export interface PromotionSettlementSummaryReq {
  settlementDateFrom: string
  settlementDateTo: string
  partnerIds?: string[]
  promotionName?: string
  serviceTypes?: string[]
  page?: number
  size?: number
}

export interface PromotionSettlementSummaryRow {
  promotionId: number
  partnerId: string
  promotionName: string
  serviceType: CodeNameMappingType
  discountType: CodeNameMappingType
  // BE 가 문자열로 내려준다 — JS Number 안전 범위를 넘는 정액을 담기 때문 (LOGISPRD-11361)
  discountValue: string
  settlementCycle: CodeNameMappingType
  // 프로모션 부과 기준(anchor)의 국가 축 파생
  country: 'KR' | 'JP' | 'GLOBAL'
  appliedDayCount: number
  quantity: number | null
  originSettlementAmount: number
  discountedSettlementAmount: number
  discountAmount: number
  // LOGISPRD-11772 — 기간(MONTHLY) 프로모션의 구간 (하한 오름차순, 일별은 빈 배열).
  // 기간은 할인값을 구간에서만 받아 discountValue 가 0 이라, 할인값 열은 이 배열로 채운다.
  // BE 미배포 구간에는 내려오지 않아 옵셔널로 둔다 (remark 와 같은 선례)
  tiers?: PromotionTier[]
}

export interface PromotionSettlementLineReq {
  // LOGISPRD-11359 — 여러 프로모션의 라인을 한 번에 받는다. 보내지 않으면 파트너·기간 내 전체.
  // 빈 배열은 BE 가 거부하므로(@Size(min=1)) 대상이 없으면 요청 자체를 하지 않는다.
  promotionIds?: number[]
  settlementDateFrom: string
  settlementDateTo: string
  // 필수 — 파트너 스코프 없이는 BE 가 일정산 테이블 월 범위 풀스캔이 된다 (BE @NotBlank)
  partnerId: string
  page?: number
  size?: number
}

// 원본 일정산 라인 — 서비스에 없는 컬럼(택배비의 상품 축·수량·단가)은 null.
// 여러 프로모션의 라인이 한 페이지에 섞이므로 프로모션 축이 행마다 실린다 (LOGISPRD-11359)
export interface PromotionSettlementLine {
  promotionId: number
  promotionName: string
  serviceType: CodeNameMappingType
  discountType: CodeNameMappingType
  // BE 가 문자열로 내려준다 — JS Number 안전 범위를 넘는 정액을 담기 때문 (LOGISPRD-11361)
  discountValue: string
  settlementCycle: CodeNameMappingType
  // 프로모션 부과 기준(anchor)의 국가 축 파생
  country: 'KR' | 'JP' | 'GLOBAL'
  settlementDate: string
  partnerId: string
  brandId: string | null
  brandName: string | null
  productBarcode: string | null
  skuId: string | null
  productNumber: string | null
  productName: string | null
  productOption: string | null
  quantity: number | null
  unitPrice: number | null
  discountedUnitPrice: number | null
  originAmount: number
  discountedAmount: number
  discountAmount: number
}

// 프로모션 엑셀 일괄 등록 (시안 v4 — 파일 선택 → 검증 → 등록 3단계)
// 계약: ticket/draft-promotion-bulk-register.md
/**
 * 양식 '프로모션 종류' 열 (일반 / 전체). 저장 축이 아니라 적용범위 입력 규칙을 가르는 입력값이다.
 * 화면 표기는 등록 화면과 같은 목록을 쓴다 (postPromotionSchemas.ts PROMOTION_KINDS)
 */
export type PromotionBulkKind = 'NORMAL' | 'ALL' | 'PRODUCT'

export interface PromotionBulkRowValue {
  country?: PromotionCountry
  orderType?: PromotionOrderType
  /** BE 가 양식에서 읽은 그대로 준다 (LOGISPRD-11492). 비었거나 표기가 틀린 칸은 null */
  kind?: PromotionBulkKind | null
  serviceType?: string
  unitType?: UnitType
  title?: string
  partnerIds?: string[]
  applyToAllPartners?: boolean
  brands?: string[]
  applyToAllBrands?: boolean
  categoryCodes?: string[]
  /** 양식 "상품(SKU)" 열. 쉼표로 여러 개, 종류가 상품일 때만 값이 있다 (LOGISPRD-14476) */
  skuIds?: string[]
  discountType?: DiscountType
  /** BE 가 문자열로 내려준다 (JsonFormat STRING, LOGISPRD-11361). 오류 행은 null */
  discountValue?: string | number | null
  settlementCycle?: SettlementCycle
  tiers?: PromotionTier[]
  startDate?: string
  endDate?: string
  remark?: string
}

export interface PromotionBulkRowError {
  // value 의 키와 같다 — 화면이 어느 칸이 틀렸는지 짚는 데 쓴다
  field: string
  code: string
  message: string
}

export interface PromotionBulkValidateRow {
  rowNo: number
  valid: boolean
  errors: PromotionBulkRowError[]
  value: PromotionBulkRowValue
}

export interface PromotionBulkValidateRes {
  summary: { total: number; valid: number; invalid: number }
  rows: PromotionBulkValidateRow[]
}

export interface PromotionBulkRegisterRes {
  registeredCount: number
  promotionIds: number[]
}

export interface PromotionSettlementLineRes {
  // 프로모션을 1건만 지정한 요청에만 채워진다 (구 단건 드릴다운 호환, LOGISPRD-11360 배포 후 BE 에서 제거)
  promotion: {
    promotionId: number
    promotionName: string
    serviceType: CodeNameMappingType
    discountType: CodeNameMappingType
    discountValue: string
    settlementCycle: CodeNameMappingType
    country: 'KR' | 'JP' | 'GLOBAL'
    startDate: string
    endDate: string
  } | null
  lines: PromotionSettlementLine[]
  pageInfo: PageInfoType
}
